import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faListAlt,
  faSyncAlt,
  faExclamationTriangle,
  faUser,
  faClock,
  faFilter,
  faSearch,
  faSignInAlt,
  faSignOutAlt,
  faFileUpload,
  faFileAlt,
  faQuoteRight,
  faCheckCircle,
  faPlay,
  faEye,
  faBuilding,
  faPlus,
  faWrench,
  faBell
} from '@fortawesome/free-solid-svg-icons';
import { getAuthHeader } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';
import './PropertyLogs.css';

/* ── Icon + style mapping for log types ──────────────────────── */
const LOG_TYPE_CONFIG = {
  check_in:          { icon: faSignInAlt,          cls: 'log-type-info',    label: 'Check-in' },
  check_out:         { icon: faSignOutAlt,          cls: 'log-type-neutral', label: 'Check-out' },
  document_uploaded: { icon: faFileUpload,          cls: 'log-type-info',    label: 'Document uploaded' },
  document_viewed:   { icon: faFileAlt,             cls: 'log-type-neutral', label: 'Document viewed' },
  quote_submitted:   { icon: faQuoteRight,          cls: 'log-type-warn',    label: 'Quote submitted' },
  quote_approved:    { icon: faCheckCircle,         cls: 'log-type-success', label: 'Quote approved' },
  quote_declined:    { icon: faExclamationTriangle, cls: 'log-type-danger',  label: 'Quote declined' },
  job_started:       { icon: faPlay,                cls: 'log-type-info',    label: 'Job started' },
  job_completed:     { icon: faCheckCircle,         cls: 'log-type-success', label: 'Job completed' },
  property_viewed:   { icon: faEye,                 cls: 'log-type-neutral', label: 'Property viewed' },
  property_edited:   { icon: faBuilding,            cls: 'log-type-warn',    label: 'Property edited' },
  access_assigned:   { icon: faUser,                cls: 'log-type-info',    label: 'Access assigned' },
  access_removed:    { icon: faUser,                cls: 'log-type-danger',  label: 'Access removed' },
  property_created:  { icon: faBuilding,            cls: 'log-type-info',    label: 'Property created' },
  inspection_passed: { icon: faCheckCircle,         cls: 'log-type-success', label: 'Inspection passed' },
  inspection_failed: { icon: faExclamationTriangle, cls: 'log-type-danger',  label: 'Inspection failed' },
  maintenance_scheduled: { icon: faWrench,          cls: 'log-type-warn',    label: 'Maintenance scheduled' },
  asset_added:       { icon: faPlus,                cls: 'log-type-info',    label: 'Asset added' },
};
const DEFAULT_CONFIG = { icon: faBell, cls: 'log-type-neutral', label: 'Activity' };

/* ── Format timestamp ────────────────────────────────────────── */
function formatTimestamp(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/* ── Skeleton loader ─────────────────────────────────────────── */
function LogSkeleton({ count = 6 }) {
  return (
    <div className="plogs-skeleton-list" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <div className="plogs-skeleton-row" key={i}>
          <div className="plogs-skeleton-dot plogs-pulse" />
          <div className="plogs-skeleton-body">
            <div className="plogs-skeleton-line plogs-skeleton-long plogs-pulse" />
            <div className="plogs-skeleton-line plogs-skeleton-med plogs-pulse" />
            <div className="plogs-skeleton-line plogs-skeleton-short plogs-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   PropertyLogs — Audit-style event log
   Renders inside PropertyDashboard <Outlet>.
   ══════════════════════════════════════════════════════════════════ */
export default function PropertyLogs() {
  const { id: propertyId } = useParams();
  const outletContext = useOutletContext() || {};
  const propertyName = outletContext.property?.name || '';

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionFilter, setActionFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  /* ── Fetch logs ─────────────────────────────────────────────── */
  const fetchLogs = async () => {
    if (!propertyId) return;
    setRefreshing(true);
    try {
      const url = getApiEndpoint(`/api/properties/${propertyId}/logs`) +
        (actionFilter !== 'all' ? `?actionType=${encodeURIComponent(actionFilter)}` : '');
      const res = await fetch(url, { headers: getAuthHeader() });
      if (!res.ok) throw new Error('Failed to load logs');
      const data = await res.json();
      setLogs(data.logs || data || []);
      setError(null);
    } catch (e) {
      console.error('Logs fetch error:', e);
      setError('Could not load logs for this property.');
      setLogs([]);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (propertyId) {
      setLoading(true);
      fetchLogs();
    }
  }, [propertyId, actionFilter]);

  /* ── Client-side search ─────────────────────────────────────── */
  const filteredLogs = useMemo(() => {
    if (!searchQuery.trim()) return logs;
    const q = searchQuery.toLowerCase();
    return logs.filter(log =>
      (log.description || '').toLowerCase().includes(q) ||
      (log.user_name || '').toLowerCase().includes(q) ||
      (log.action_type || '').toLowerCase().includes(q)
    );
  }, [logs, searchQuery]);

  /* ── Distinct action types for filter ───────────────────────── */
  const availableTypes = useMemo(() => {
    const types = new Set(logs.map(l => l.action_type).filter(Boolean));
    return [...types].sort();
  }, [logs]);

  /* ── No property selected ───────────────────────────────────── */
  if (!propertyId) {
    return (
      <div className="plogs-page">
        <div className="plogs-error" role="alert">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>Open a property to view its logs.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="plogs-page">
      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="plogs-header">
        <div className="plogs-title-row">
          <div className="plogs-title-icon" aria-hidden="true">
            <FontAwesomeIcon icon={faListAlt} />
          </div>
          <div>
            <h1>Event Log</h1>
            {propertyName && <p className="plogs-subtitle">{propertyName}</p>}
          </div>
        </div>
      </div>

      {/* ── Error banner ───────────────────────────────────────── */}
      {error && (
        <div className="plogs-error" role="alert">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>{error}</span>
          <button className="plogs-retry-btn" onClick={fetchLogs}>
            <FontAwesomeIcon icon={faSyncAlt} /> Retry
          </button>
        </div>
      )}

      {/* ── Toolbar ────────────────────────────────────────────── */}
      <div className="plogs-toolbar">
        <div className="plogs-toolbar-left">
          <FontAwesomeIcon icon={faFilter} className="plogs-filter-icon" aria-hidden="true" />
          <select
            value={actionFilter}
            onChange={e => setActionFilter(e.target.value)}
            className="plogs-type-select"
            aria-label="Filter logs by type"
          >
            <option value="all">All types</option>
            {availableTypes.map(type => {
              const cfg = LOG_TYPE_CONFIG[type];
              return (
                <option key={type} value={type}>
                  {cfg ? cfg.label : type.replace(/_/g, ' ')}
                </option>
              );
            })}
          </select>
        </div>

        <div className="plogs-search-wrap">
          <FontAwesomeIcon icon={faSearch} className="plogs-search-icon" aria-hidden="true" />
          <input
            type="text"
            placeholder="Search logs..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="plogs-search-input"
            aria-label="Search logs"
          />
        </div>

        <div className="plogs-toolbar-right">
          <button
            type="button"
            className="plogs-refresh-btn"
            onClick={fetchLogs}
            disabled={refreshing}
            aria-label="Refresh logs"
          >
            <FontAwesomeIcon icon={faSyncAlt} spin={refreshing} />
            <span className="plogs-refresh-label">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
          </button>
          <span className="plogs-count">
            {filteredLogs.length} {filteredLogs.length === 1 ? 'entry' : 'entries'}
          </span>
        </div>
      </div>

      {/* ── Loading skeleton ───────────────────────────────────── */}
      {loading && <LogSkeleton count={6} />}

      {/* ── Empty state ────────────────────────────────────────── */}
      {!loading && !error && filteredLogs.length === 0 && (
        <div className="plogs-empty">
          <FontAwesomeIcon icon={faListAlt} className="plogs-empty-icon" aria-hidden="true" />
          <h3>No log entries</h3>
          <p>
            {searchQuery
              ? 'No results match your search. Try a different keyword.'
              : 'Events will appear here as actions are taken on this property — check-ins, uploads, inspections, and more.'}
          </p>
        </div>
      )}

      {/* ── Log table / timeline ───────────────────────────────── */}
      {!loading && filteredLogs.length > 0 && (
        <div className="plogs-timeline">
          <ul className="plogs-list" role="list">
            {filteredLogs.map(log => {
              const config = LOG_TYPE_CONFIG[log.action_type] || DEFAULT_CONFIG;
              return (
                <li key={log.id} className={`plogs-item ${config.cls}`}>
                  <div className="plogs-item-dot" aria-hidden="true">
                    <FontAwesomeIcon icon={config.icon} />
                  </div>
                  <div className="plogs-item-content">
                    <p className="plogs-item-desc">
                      {log.description || config.label || 'Event'}
                    </p>
                    <div className="plogs-item-meta">
                      <span className={`plogs-type-badge ${config.cls}`}>
                        {config.label}
                      </span>
                      {log.user_name && (
                        <span className="plogs-meta-user">
                          <FontAwesomeIcon icon={faUser} /> {log.user_name}
                        </span>
                      )}
                      <time
                        className="plogs-meta-time"
                        dateTime={log.timestamp ? new Date(log.timestamp).toISOString() : undefined}
                      >
                        <FontAwesomeIcon icon={faClock} /> {formatTimestamp(log.timestamp)}
                      </time>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
